import { createContext, useContext, useMemo, useReducer } from "react"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"

export const KPIContext = createContext()

const PREFERENCE_SUBTYPE_BASE = "kpi"

function getPreferenceSubType(panelId) {
  return panelId ? `${PREFERENCE_SUBTYPE_BASE}.${panelId}` : PREFERENCE_SUBTYPE_BASE
}

const DEFAULT_STATE = {
  sectionSettings: {},
  selectedGroup: null,
  selectedTab: "summary",
}

// Define which fields should be saved as preferences
const PREFERENCE_FIELDS = [
  "sectionSettings",
  "selectedGroup",
  "selectedTab",
]

function reducer(current, action) {
  return produce(current, draft => {
    switch (action.type) {
      case "setSectionExpanded":
        draft.sectionSettings[action.key] = action.value
        break
      case "setSelectedGroup":
        draft.selectedGroup = action.group
        break
      case "setSelectedTab":
        draft.selectedTab = action.tab
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setPreferences(preferences) {
  return { type: "setPreferences", preferences }
}

export default function useKPIContext(parentContext, options = {}) {
  const { initialTab, panelId } = options
  const parentContextValue = useContext(parentContext)
  const preferenceSubType = useMemo(() => getPreferenceSubType(panelId), [panelId])
  const initialState = useMemo(
    () => ({
      ...DEFAULT_STATE,
      ...(initialTab != null && {
        selectedTab: initialTab,
      }),
    }),
    [initialTab],
  )
  const [state, dispatch] = useReducer(reducer, initialState)

  const isPreferencesLoaded = usePreferences(
    preferenceSubType,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
    {
      fallbackSubType: panelId ? PREFERENCE_SUBTYPE_BASE : undefined,
      ...(initialTab != null && { omitFallbackKeys: ["selectedTab"] }),
    },
  )

  const contextUpdaters = useMemo(() => ({
    setSectionExpanded: ({ key, value }) => dispatch({ type: "setSectionExpanded", key, value }),
    setSelectedGroup: (group) => dispatch({ type: "setSelectedGroup", group }),
    setSelectedTab: (tab) => dispatch({ type: "setSelectedTab", tab }),
  }), [dispatch])

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...state,
    ...contextUpdaters,
    isPreferencesLoaded,
  }), [state, parentContextValue, contextUpdaters, isPreferencesLoaded])

  return [KPIContext, contextValue]
}
